var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getState, num } from './helpers';
import { resolveBounds } from './layout';
import { updateAutoWidgetProps } from './refresh';
function createResizeObserver(callback) {
    if (typeof ResizeObserver !== 'undefined') {
        return new ResizeObserver(callback);
    }
    return {
        observe: function () { },
        unobserve: function () { },
        disconnect: function () { },
    };
}
var observers = new WeakMap();
function readContainerSize(container) {
    var w = container.clientWidth;
    var h = container.clientHeight;
    if (w <= 0 || h <= 0) {
        var rect = container.getBoundingClientRect();
        if (w <= 0)
            w = rect.width;
        if (h <= 0)
            h = rect.height;
    }
    if (w <= 0 || h <= 0) {
        var sw = parseFloat(container.style.width);
        var sh = parseFloat(container.style.height);
        if (w <= 0 && Number.isFinite(sw) && sw > 0)
            w = sw;
        if (h <= 0 && Number.isFinite(sh) && sh > 0)
            h = sh;
    }
    return { w: w, h: h };
}
/** Compute width/height/size patch from a container element. */
export function fitAutoWidgetToContainer(widgetNode, containerW, containerH, pad) {
    if (pad === void 0) { pad = 8; }
    var state = getState(widgetNode);
    var merged = __assign({}, state);
    if (containerW > 0)
        merged.width = Math.max(56, Math.floor(containerW));
    if (containerH > 0)
        merged.height = Math.max(44, Math.floor(containerH));
    var bounds = resolveBounds(merged, num(state, 'width', 160), num(state, 'height', 120), pad);
    var patch = {};
    if (bounds.width !== num(state, 'width', 0))
        patch.width = bounds.width;
    if (bounds.height !== num(state, 'height', 0))
        patch.height = bounds.height;
    var hadSize = num(state, 'size', 0) > 0 || 'size' in state;
    if (hadSize && bounds.dialSize !== num(state, 'size', 0)) {
        patch.size = bounds.dialSize;
    }
    if (Object.keys(patch).length === 0) {
        return {};
    }
    return patch;
}
/** Keep automotive widget props in sync when a container element is resized. */
export function installAutoWidgetResizeObserver(widgetNode, container, options) {
    var _a, _b, _c;
    if (options === void 0) { options = {}; }
    detachAutoWidgetResizeObserver(widgetNode);
    var minW = (_a = options.minWidth) !== null && _a !== void 0 ? _a : 72;
    var minH = (_b = options.minHeight) !== null && _b !== void 0 ? _b : 56;
    var pad = (_c = options.padding) !== null && _c !== void 0 ? _c : 0;
    var apply = function (rawW, rawH) {
        var _a;
        var state = getState(widgetNode);
        var w = rawW > 0
            ? Math.max(minW, Math.floor(rawW - pad))
            : num(state, 'width', 0);
        var h = rawH > 0
            ? Math.max(minH, Math.floor(rawH - pad))
            : num(state, 'height', 0);
        if (w < 8 || h < 8)
            return;
        var last = widgetNode.metadata._lastAutoContainerSize;
        if (last && last.w === w && last.h === h)
            return;
        widgetNode.metadata._lastAutoContainerSize = { w: w, h: h };
        var patch = fitAutoWidgetToContainer(widgetNode, w, h, pad);
        if (Object.keys(patch).length === 0)
            return;
        updateAutoWidgetProps(widgetNode, patch);
        (_a = options.onResize) === null || _a === void 0 ? void 0 : _a.call(options, w, h);
    };
    var raf = 0;
    var ro = createResizeObserver(function (entries) {
        var _a;
        var rect = (_a = entries[0]) === null || _a === void 0 ? void 0 : _a.contentRect;
        if (!rect)
            return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () { return apply(rect.width, rect.height); });
    });
    ro.observe(container);
    observers.set(widgetNode, ro);
    widgetNode.metadata.resizeObserverAttached = true;
    var initial = readContainerSize(container);
    apply(initial.w, initial.h);
}
export function detachAutoWidgetResizeObserver(widgetNode) {
    var ro = observers.get(widgetNode);
    ro === null || ro === void 0 ? void 0 : ro.disconnect();
    observers.delete(widgetNode);
    delete widgetNode.metadata._lastAutoContainerSize;
    delete widgetNode.metadata.resizeObserverAttached;
}
